import { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';

const achievements = [
  { icon: 'ri-shield-check-line', title: '安全可靠', desc: '所有黄游下载资源经过严格的安全检测，确保玩家下载的游戏文件安全无毒。采用多重检测引擎，定期更新安全策略。' },
  { icon: 'ri-flashlight-line', title: '极速下载', desc: '多节点CDN加速，全球覆盖下载节点，确保黄游下载速度稳定快速。支持断点续传，不怕下载中断。' },
  { icon: 'ri-community-line', title: '丰富资源', desc: '收录500+款精选成人游戏，涵盖RPG、视觉小说、模拟经营等多种类型，定期更新黄游下载合集。' },
  { icon: 'ri-user-star-line', title: '用户体验', desc: '一键下载无需注册，简洁直观的界面设计。15万+活跃用户的选择，黄游下载满意度持续领先。' },
];

const milestones = [
  { year: '2024', title: '平台创立', desc: '黄游下载平台正式上线，致力于提供安全、正版的成人游戏下载服务。' },
  { year: '2024 Q3', title: '资源突破', desc: '黄游下载资源库突破100款游戏，覆盖主流游戏类型。' },
  { year: '2025 Q1', title: '用户增长', desc: '累计用户突破10万+，黄游下载总量超过100万次。' },
  { year: '2025 Q2', title: '安全升级', desc: '引入AI安全检测引擎，黄游下载安全性达到行业领先水平。' },
  { year: '2025 Q3', title: '移动拓展', desc: '手机黄游下载频道上线，支持安卓和iOS双平台。资源库突破500+。' },
];

const testimonials = [
  {
    name: '游戏玩家小王',
    text: '找了好久才找到这个黄游下载平台，资源丰富而且下载速度快。最关键的是安全可靠，不用担心病毒问题。',
    rating: 5,
  },
  {
    name: 'ACG爱好者老张',
    text: '黄游下载合集的更新速度很快，每次都能在这里找到最新的游戏。界面简洁，分类清晰，强烈推荐！',
    rating: 5,
  },
  {
    name: '手游玩家小李',
    text: '手机黄游下载太方便了，安卓和iOS都能用。之前在其他地方下载经常遇到问题，这个平台真的很靠谱。',
    rating: 5,
  },
  {
    name: 'RPG资深玩家',
    text: 'PC成人游戏下载资源很全，各种类型的都有。安全检测也很到位，下载的游戏都能正常安装运行。',
    rating: 4,
  },
];

export default function AboutPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黄游下载',
    url: 'https://huangyou-download.com',
    description: '正版成人游戏资源安全获取平台。提供安全、快速、正版黄游下载服务。',
    email: 'contact@huangyou-download.com',
    foundingDate: '2024',
    knowsAbout: ['黄游下载', '成人游戏下载', '手机黄游下载', 'PC成人游戏下载'],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@huangyou-download.com',
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 黄游下载',
    description: '黄游下载品牌介绍、平台信誉和联系方式。了解黄游下载的发展历程、核心价值观和用户评价。',
    url: 'https://huangyou-download.com/about',
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setSubmitLoading(true);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();

    if (honeypot) {
      setFormSubmitted(true);
      setSubmitLoading(false);
      return;
    }

    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const message = formData.get('message') as string;

    if (!name || !email || !message) {
      setFormError('请填写所有必填字段');
      setSubmitLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setFormError('请输入有效的邮箱地址');
      setSubmitLoading(false);
      return;
    }

    if (message.length > 500) {
      setFormError('留言内容不能超过500字');
      setSubmitLoading(false);
      return;
    }

    const body = new URLSearchParams();
    body.append('name', name);
    body.append('email', email);
    body.append('message', message);

    fetch('https://readdy.ai/api/form/d9qui7snlsngrm4lm6d0', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    })
      .then((response) => response.text())
      .then((responseText) => {
        try {
          const parsed = JSON.parse(responseText);
          if (parsed?.code === 'OK') {
            setFormSubmitted(true);
            form.reset();
          } else {
            const errMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
            setFormError(errMsg.includes('spam') || errMsg.includes('form data is spam')
              ? '提交失败，请稍后重试'
              : errMsg || '提交失败，请稍后重试');
          }
        } catch {
          setFormError('提交失败，请检查网络后重试');
        }
      })
      .catch(() => {
        setFormError('网络错误，请稍后重试');
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  useEffect(() => {
    document.title = '关于我们 - 黄游下载 | 品牌介绍与联系方式';
  }, []);

  return (
    <>
      <SEOHead
        title="关于我们 - 黄游下载"
        description="了解黄游下载平台的发展历程、核心价值观、品牌信誉和联系方式。正版成人游戏资源安全获取平台。"
        keywords="关于黄游下载,黄游下载品牌,成人游戏下载平台,联系黄游下载"
        canonicalUrl="/about"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Page Header */}
          <section className="relative w-full h-[240px] md:h-[300px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20office%20workspace%20with%20warm%20ambient%20lighting%2C%20clean%20minimalist%20design%2C%20soft%20gradient%20background%2C%20professional%20atmosphere%2C%20orange%20and%20warm%20neutral%20tones&width=1200&height=400&seq=about-hero-bg&orientation=landscape"
              alt="关于黄游下载"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
            <div className="relative z-10 text-center pt-16">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
                关于我们
              </h1>
              <p className="text-sm md:text-base text-white/75">了解黄游下载的品牌故事与发展历程</p>
            </div>
          </section>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          {/* Brand Introduction */}
          <section className="py-10 md:py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-8 md:mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  品牌介绍
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                  黄游下载成立于2024年，是国内领先的成人游戏资源安全获取平台。我们致力于为广大玩家提供安全、快速、正版的黄游下载服务。平台收录了500+款精选成人游戏，涵盖RPG角色扮演、视觉小说、模拟经营、动作冒险等多种类型，满足不同玩家的黄游下载需求。
                </p>
              </div>

              {/* Achievement Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-12">
                {achievements.map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100/80 shrink-0">
                      <i className={`${item.icon} text-lg text-accent-700`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1.5">{item.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mission */}
              <div className="text-center p-8 rounded-lg bg-background-100 border border-background-200/70 mb-12">
                <h3 className="font-heading text-xl font-bold text-foreground-950 mb-4">我们的使命</h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-xl mx-auto">
                  以安全为核心，以品质为标准，为每一位玩家提供值得信赖的黄游下载服务。我们相信，优质的成人游戏体验始于安全的下载环境。黄游下载将持续优化平台服务，丰富资源库，让更多玩家享受安全便捷的成人游戏下载体验。
                </p>
              </div>
            </div>
          </section>

          {/* Development Timeline */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-8 md:mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  发展历程
                </h2>
                <p className="text-sm text-foreground-600">黄游下载从创立到成长的每一步</p>
              </div>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-accent-200 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {milestones.map((m, i) => (
                    <div key={i} className={`relative flex items-start gap-6 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 ring-4 ring-accent-100 -translate-x-1/2 mt-1.5 z-10"></div>
                      <div className={`ml-10 md:ml-0 md:w-1/2 ${i % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:pl-10'}`}>
                        <div className="p-4 rounded-lg bg-background-50 border border-background-200/70">
                          <span className="text-xs font-medium text-accent-600 bg-accent-100/80 px-2 py-0.5 rounded-full">{m.year}</span>
                          <h4 className="font-heading text-base font-semibold text-foreground-900 mt-2 mb-1">{m.title}</h4>
                          <p className="text-sm text-foreground-600">{m.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-10 md:py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-8 md:mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  用户评价
                </h2>
                <p className="text-sm text-foreground-600">来自真实玩家的反馈与认可</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {testimonials.map((t, i) => (
                  <div key={i} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, starI) => (
                        <span key={starI} className="w-3 h-3 flex items-center justify-center">
                          <i className={`text-xs ${starI < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'}`}></i>
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-3">{t.text}</p>
                    <p className="text-xs text-foreground-500">— {t.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-2xl mx-auto px-4 md:px-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600">如有任何问题或建议，欢迎通过以下方式联系我们</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100/80">
                    <i className="ri-mail-line text-lg text-accent-700"></i>
                  </div>
                  <p className="text-sm font-medium text-foreground-800">邮箱</p>
                  <p className="text-xs text-foreground-500">contact@huangyou-download.com</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100/80">
                    <i className="ri-time-line text-lg text-accent-700"></i>
                  </div>
                  <p className="text-sm font-medium text-foreground-800">工作时间</p>
                  <p className="text-xs text-foreground-500">周一至周五 9:00-18:00</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100/80">
                    <i className="ri-message-2-line text-lg text-accent-700"></i>
                  </div>
                  <p className="text-sm font-medium text-foreground-800">响应时间</p>
                  <p className="text-xs text-foreground-500">24小时内回复</p>
                </div>
              </div>

              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
                {formSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100/80">
                      <i className="ri-check-line text-2xl text-accent-600"></i>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">提交成功！</h3>
                    <p className="text-sm text-foreground-600">感谢您的留言，我们会在24小时内回复您。</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-readdy-form="d9qui7snlsngrm4lm6d0">
                    <input
                      type="text"
                      name="phone_alt"
                      className="anti-bot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors duration-200 resize-none"
                        placeholder="请输入您的留言内容（不超过500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">不超过500字</p>
                    </div>

                    {formError && (
                      <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-md">{formError}</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitLoading}
                      className="px-6 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitLoading ? '提交中...' : '提交留言'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </section>
        </main>

        <Footer />

        <script type="application/ld+json">{JSON.stringify([orgSchema, aboutSchema])}</script>
      </div>
    </>
  );
}